import React from 'react';
import { Accordion, AccordionSummary, AccordionDetails, Typography, Button } from '@material-ui/core';
import ExploreIcon from '@material-ui/icons/Explore';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { BulletItem } from '../utils/BulletItem';
import { DividerWithLabel } from '../utils/DividerWithLabel';
import { PocBox } from '../utils/PocBox';

export default function StepPrerequisites({ classes }: { classes: any }) {
  return (
    <>
      <Typography variant="body2" style={{ color: '#757575', marginBottom: 20 }}>
        Before submitting your onboarding request, you must gather all required information across three areas: Business Case, Entitlements, and APIGEE configuration. Having these ready ensures a smooth, single-pass submission.
      </Typography>

      {/* ── Section 1: Business Case ─────────────────────────────────── */}
      <DividerWithLabel label="Business Case" classes={classes} />

      <Typography variant="body2" style={{ color: '#9E9E9E', marginBottom: 12, marginTop: 4 }}>
        A business case justifies your API access request. It must clearly define what data is consumed, by whom, and for what purpose.
      </Typography>

      <Accordion className={classes.subAccordion} defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography className={classes.subAccordionSummary}>API Endpoints</Typography>
        </AccordionSummary>
        <AccordionDetails className={classes.subAccordionDetails} style={{ flexDirection: 'column' }}>
          <BulletItem text="List exact API endpoints (e.g., /catDigital/assets/v2/assets)." classes={classes} />
          <BulletItem text="Specify HTTP methods (GET, POST, PUT, DELETE) for each endpoint." classes={classes} />
          <div style={{ marginTop: 12 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<ExploreIcon />}
              component="a"
              href="/catalog"
              style={{ textTransform: 'none', borderRadius: 8, fontSize: 13 }}
            >
              Explore APIs
            </Button>
          </div>
        </AccordionDetails>
      </Accordion>

      <Accordion className={classes.subAccordion}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography className={classes.subAccordionSummary}>Consumer Details</Typography>
        </AccordionSummary>
        <AccordionDetails className={classes.subAccordionDetails} style={{ flexDirection: 'column' }}>
          <BulletItem text="Consumer type: UI, Backend Service, or Standalone Service." classes={classes} />
          <BulletItem text="Consumer audience: CAT internal, Dealer, or End Customer." classes={classes} />
          <BulletItem text="Application name and team responsible for consumption." classes={classes} />
        </AccordionDetails>
      </Accordion>

      <Accordion className={classes.subAccordion}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography className={classes.subAccordionSummary}>Data Usage & Functionality</Typography>
        </AccordionSummary>
        <AccordionDetails className={classes.subAccordionDetails} style={{ flexDirection: 'column' }}>
          <BulletItem text="Describe the end-user feature or business capability these APIs will power (e.g., asset tracking dashboard, automated testing)." classes={classes} />
          <BulletItem text="Explain how the API responses will be used within this functionality, rendered in a UI or processed by another service." classes={classes} />
          <BulletItem text="How will the data from these APIs be consumed and applied in your implementation?" classes={classes} />
        </AccordionDetails>
      </Accordion>

      <Accordion className={classes.subAccordion}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography className={classes.subAccordionSummary}>Technical Documentation</Typography>
        </AccordionSummary>
        <AccordionDetails className={classes.subAccordionDetails} style={{ flexDirection: 'column' }}>
          <BulletItem text="Attach AVTs or Business Features or HLAs in the Business case." classes={classes} />
          <BulletItem text="Which Program is this related to? What is the Tenrox code?" classes={classes} />
        </AccordionDetails>
      </Accordion>

      <div className={classes.formulaBox} style={{ marginBottom: 8 }}>
        <Typography className={classes.formulaPart}>APIs</Typography>
        <Typography className={classes.formulaOp}>+</Typography>
        <Typography className={classes.formulaPart}>Consumers</Typography>
        <Typography className={classes.formulaOp}>+</Typography>
        <Typography className={classes.formulaPart}>Usage</Typography>
        <Typography className={classes.formulaOp}>+</Typography>
        <Typography className={classes.formulaPart}>Flow</Typography>
        <Typography className={classes.formulaOp}>=</Typography>
        <Typography style={{ color: '#FFFFFF', fontWeight: 800, fontSize: 16 }}>Business Case ✓</Typography>
      </div>

      {/* ── Section 2: Entitlements ──────────────────────────────────── */}
      <DividerWithLabel label="Entitlements" classes={classes} />

      <Typography variant="body2" style={{ color: '#9E9E9E', marginBottom: 12, marginTop: 4 }}>
        Entitlements control which applications and clients have access to which APIs. The required details depend on your client type.
      </Typography>

      <Accordion className={classes.subAccordion}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography className={classes.subAccordionSummary}>Application Onboarding Target</Typography>
        </AccordionSummary>
        <AccordionDetails className={classes.subAccordionDetails} style={{ flexDirection: 'column' }}>
          <BulletItem text="Provide the registered application name in the Entitlements portal." classes={classes} />
          <BulletItem text="If first time accessing Helios Platform APIs, provide App Name, LeanIX ID, and HLA for new application onboarding." classes={classes} />
          <BulletItem text="If accessing own API, use PFM role for the service testing." classes={classes} />
        </AccordionDetails>
      </Accordion>

      <Accordion className={classes.subAccordion}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography className={classes.subAccordionSummary}>Client-Specific Details</Typography>
        </AccordionSummary>
        <AccordionDetails className={classes.subAccordionDetails} style={{ flexDirection: 'column' }}>
          <Typography variant="body2" style={{ fontWeight: 600, marginBottom: 12 }}>For CC Clients:</Typography>
          <BulletItem text="Provide the service role to map to client id." classes={classes} />
          <BulletItem text="Include the party number to filter the response." classes={classes} />
          <BulletItem text="Confirm which API domains the role grants access to." classes={classes} />
          
          <Typography variant="body2" style={{ fontWeight: 600, marginTop: 16, marginBottom: 12 }}>For Pseudo User Clients:</Typography>
          <BulletItem text="Provide the catrecid for the pseudo user." classes={classes} />
          <BulletItem text="List the roles assigned to the pseudo user for testing." classes={classes} />
          <BulletItem text="Confirm TESTID is added to non-PROD clients." classes={classes} />
          <BulletItem text="Include the party number to filter the response." classes={classes} />
        </AccordionDetails>
      </Accordion>

      <PocBox
        classes={classes}
        pocs={[
          { contact: 'Cosmos Team', email: 'service_adoption_team_cosmos@cat.com' },
          { contact: 'Entitlements Team', email: 'entitlementonboardingGroup@cat.com' },
          { contact: 'APIGEE Team', email: 'api_management@cat.com' },
        ]}
        issues={[
          'Incomplete endpoint list',
          'Missing consumer type',
          'Missing data usage details',
          'Unclear business capability',
          'Missing party number',
          'Role not found',
          'Product not found in APIGEE',
        ]}
      />
    </>
  );
}
